import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { t } from 'i18next';

import { ToggleStoreState, ToggleStore } from '../../stores/ToggleStore';
import { UIHelper } from '../../utils/UIHelper';
import { MailHelper } from '../../utils/MailHelper';
import { AppConfigurationStore, DownloadOption } from '../../stores/AppConfigurationStore';
import { ViewSettingsActions } from '../../actions/ViewSettingsActions';
import { MessageBoxType, SidebarModule } from '../../constants/Enums';
import { LoadingCancelable } from '../LoadingCancelable';
import { UICoreButton } from '../UICoreButton';
import { LongButton } from '../LongButton';
import { ToggleGroup } from '../ToggleGroup';
import { DownloadButton } from '../DownloadButton';
import { ToggleActions } from '../../actions/ToggleActions';
import { ShareBaseTab } from './ShareBaseTab';
import { composeMail, MailOptions } from '../../submodules/app-api/AppAction';
import { MailBrowserHelper } from '../../utils/MailBrowserHelper';
import { ViewSettingsStore } from '../../stores/ViewSettingsStore';

interface ShareMailState {
    exportSettings: ToggleStoreState;
}

export class ShareMail extends ShareBaseTab<ShareMailState> {
    public componentWillMount(): void {
        this.addStoreListener(ToggleStore, this.onChange);
        this.onChange();
    }

    public componentDidMount(): void {
        UIHelper.updateScrollbar(ReactDOM.findDOMNode(this) as Element);
    }

    public componentDidUpdate(): void {
        UIHelper.updateScrollbar(ReactDOM.findDOMNode(this) as Element);
    }

    public render(): JSX.Element | null {
        if (!this.props.visible) {
            return null;
        }
        return (
            <div className="sidebar-tab-container" key={this.props.layoutType.toString()}><div className="scrollbar-wcf">
                {this.header(t('Mail'))}
                <div className="sidebar-content standard-header">
                    <div className={'long-button-group' + (AppConfigurationStore.getState().inApp ? '' : ' with-title active')}>
                        <LongButton
                            shortBorder={true}
                            toggled={(this.state.exportSettings.indexOf('IMAGE') !== -1)}
                            onClick={() => {ToggleActions.toggleType('IMAGE'); }}
                        >
                            {t('Image')}
                        </LongButton>
                    </div>
                    {AppConfigurationStore.getState().inApp ? <div className="sidebar-subheader">{t('Commercial Information')}</div> : null}
                    <ToggleGroup title={t('Commercial Information')}>
                        {AppConfigurationStore.getState().articleListTypes.map((option: DownloadOption, index: number) => {
                            return (
                                <DownloadButton
                                    key={index + '-al'}
                                    option={option}
                                    callback={option.type === 'PDF' ? () => { /**/ } : ToggleActions.toggleType}
                                    toggled={this.state.exportSettings.indexOf(option.type) !== -1}
                                />
                            );
                        })}
                        {AppConfigurationStore.getState().commercialDownloadTypes.map((option: DownloadOption, index: number) => {
                            return (
                                <DownloadButton
                                    key={index + '-com'}
                                    option={option}
                                    callback={ToggleActions.toggleType}
                                    toggled={this.state.exportSettings.indexOf(option.type) !== -1}
                                />
                            );
                        })}
                    </ToggleGroup>
                    {AppConfigurationStore.getState().inApp ? <div className="sidebar-subheader">{t('CAD Symbols')}</div> : null}
                    <ToggleGroup title={t('CAD Symbols')}>
                        {AppConfigurationStore.getState().cadDownloadTypes.map((option: DownloadOption, index: number) => {
                            return (
                                <DownloadButton
                                    key={index + '-cad'}
                                    option={option}
                                    callback={ToggleActions.toggleType}
                                    toggled={this.state.exportSettings.indexOf(option.type) !== -1}
                                />
                            );
                        })}
                    </ToggleGroup>
                    {this.button()}
                </div>
            </div></div>
        );
    }

    protected onChange = (): void => {
        this.setState({
            exportSettings: ToggleStore.getState()
        });
    };

    protected submit = (): void => {
        let resolveFromOutside: () => void;
        let killPromise: boolean = false;
        const keepAliveCheck: () => boolean = () => {
            return killPromise;
        };
        const mailCreatingPromise: Promise<void> = new Promise<void>((resolve) => {
            resolveFromOutside = resolve;
            return UIHelper.timeout().then(async (): Promise<void> => {
                if (AppConfigurationStore.getState().inApp) {
                    const options: MailOptions | null = await MailHelper.getMailOptions(this.state.exportSettings, keepAliveCheck);
                    if (options != null) {
                        composeMail(options);
                    }
                    resolve();
                } else {
                    const mailLink: string | null = await MailBrowserHelper.getMailToLink(this.state.exportSettings, keepAliveCheck);
                    if (mailLink == null) {
                        resolve();
                        return;
                    }
                    ViewSettingsActions.changeSidebar(SidebarModule.NONE);
                    if (ViewSettingsStore.getBrowser() === 'Safari') {
                        // Note: wrapping `mailto` links inside iframes does not work with safari; more details can be found in the README file
                        window.location.href = mailLink;
                    } else {
                        ViewSettingsActions.showMessage({
                            wrap: false,
                            element: <iframe style={{display: 'none'}} key={Date.now()} src={mailLink} width="1" height="1" frameBorder="0"/>,
                            type: MessageBoxType.DEFAULT
                        });
                    }
                    resolve();
                }
            });
        });

        ViewSettingsActions.withLock<void>(
            () => { return mailCreatingPromise; },
            <LoadingCancelable cancelCallback={() => { resolveFromOutside(); killPromise = true; }} label={t('Processing')}/>
        );
    };

    protected button = (): React.ReactNode => {
        if (this.state.exportSettings.length > 0) {
            return (
                <React.Fragment>
                    <UICoreButton
                        color="green"
                        align="block"
                        key="send-mail"
                        onClick={this.submit}
                        label={t('Send e-mail')}
                    />
                    <UICoreButton color="green" align="block" key="send-request" onClick={this.openShareFormular} label={t('Send request')} />
                </React.Fragment>
            );
        }

        return (
            <React.Fragment>
                <UICoreButton color="light-grey" align="block" key="send-mail" label={t('Send e-mail')}/>
                <UICoreButton color="light-grey" align="block" key="send-request" label={t('Send request')}/>
            </React.Fragment>
        );
    };

    protected openShareFormular = (): void => {
        ViewSettingsActions.changeSidebar(SidebarModule.CUSTOM_SHARE_FORMULAR);
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/sidebar/ShareMail.tsx